import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import clsx from 'clsx';
import useApartmentTemporary from '../../api/hooks/use-apartment-temporary';
import { MarketTypes } from '../../api/types';
import { BuyApartmentFlowPaths } from 'routes';
import { toggleTemporary } from 'api';
import { ApartmentDeal } from 'api/hooks/use-apartment-deals';
import { formatDealWord } from 'lib/utils';
import { ITEMS_PER_PAGE } from 'lib/constants';
import Loader from 'components/loader';
import NotFound from 'components/not-found';
import IconButton from 'components/icon-button';
import ErrorMessage from 'components/error-message';
import { ApartmentNavigateProps } from 'screens/apartment';
import BaseScreen, { BASE_SCREEN_HEADER_HEIGHT } from 'components/base-screen';
import LoadMoreVirtualListFooter from 'components/load-more-virtual-list-footer';
import ApartmentListItem, { APARTMENT_LIST_ITEM_HEIGHT } from 'components/apartment-list-item';
import temporary from 'assets/images/temporary.svg';
import temporaryYellow from 'assets/images/temporary-yellow.svg';
import styles from './styles.module.scss';

const Temporary: React.FC = () => {
  const navigate = useNavigate();
  const listStyle = { height: `calc(100vh - ${BASE_SCREEN_HEADER_HEIGHT})` };

  const {
    data: apartments,
    total,
    isInitialLoading,
    isLoadingMore,
    setSize,
    error,
    mutate,
  } = useApartmentTemporary();

  const handleTemporaryToggle = (apartment: ApartmentDeal) => async (e: React.MouseEvent) => {
    e.stopPropagation();

    await toggleTemporary(apartment, mutate);
  };

  const loadMore = useCallback(() => {
    return setTimeout(() => {
      setSize((s) => s + 1);
    }, 200);
  }, [setSize]);

  useEffect(() => {
    const timeout = loadMore();
    return () => clearTimeout(timeout);
  }, []);

  return (
    <BaseScreen title={formatDealWord(total)}>
      {error && apartments.length === 0 ? (
        <ErrorMessage className={styles.margined} message={error} />
      ) : isInitialLoading ? (
        <Loader className={styles.margined} />
      ) : apartments.length > 0 ? (
        <Virtuoso
          useWindowScroll
          context={{
            total,
            loading: isLoadingMore,
            per: ITEMS_PER_PAGE,
            loadedNum: apartments.length,
            loadMore: () => setSize((s) => s + 1),
          }}
          data={apartments}
          style={listStyle}
          className={styles.container}
          defaultItemHeight={APARTMENT_LIST_ITEM_HEIGHT}
          endReached={() => {
            if (apartments.length !== total) {
              loadMore();
            }
          }}
          itemContent={(index, apartment) => (
            <ApartmentListItem
              {...apartment}
              onClick={() =>
                navigate(
                  apartment?.marketType === MarketTypes.SECONDARY
                    ? BuyApartmentFlowPaths.apartmentSecondary
                    : BuyApartmentFlowPaths.apartment,
                  {
                    state: { id: apartment?.id, isTemporary: true } as ApartmentNavigateProps,
                  },
                )
              }
              icon={
                <IconButton
                  // @ts-ignore
                  onClick={handleTemporaryToggle(apartment)}
                  icon={apartment?.isSavedInTemporaryFiles ? temporaryYellow : temporary}
                />
              }
              className={clsx(index !== apartments?.length - 1 && styles.border)}
              isHide={apartment?.hiddenUntil}
            />
          )}
          components={{ Footer: LoadMoreVirtualListFooter }}
        />
      ) : (
        <NotFound />
      )}
    </BaseScreen>
  );
};

export default Temporary;
